import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Chip, Box } from '@mui/material';
import { Save, AddCircle } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import { EnvironmentClient, EnvironmentModel, ServiceConnectionClient, UpdateEnvironmentRequest } from '../../api/api';
import { useApiClient, useToastService } from '@@common';
import { getCloudProviderDefinition } from './Components/Consts';

interface EditEnvironmentDialogProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	environment: EnvironmentModel | null;
	onSuccess: () => void;
}

export default function EditEnvironmentDialog({ open, setOpen, environment, onSuccess }: EditEnvironmentDialogProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const serviceConnectionClient = useApiClient(ServiceConnectionClient) as ServiceConnectionClient;
	const toastService = useToastService();

	const [isSaving, setIsSaving] = useState(false);
	const [formData, setFormData] = useState<UpdateEnvironmentRequest>({
		name: '',
		tags: [],
	});
	const [tagInput, setTagInput] = useState('');
	const { data: primaryServiceConnection, isLoading: isLoadingPrimaryServiceConnection } = useQuery({
		queryKey: ['environmentPrimaryServiceConnection', environment?.serviceConnectionId],
		queryFn: async () => {
			const result = await serviceConnectionClient.search({ ids: [environment!.serviceConnectionId] });
			return result.items[0] ?? null;
		},
		enabled: open && !!environment?.serviceConnectionId,
	});
	const deploymentProvider = getCloudProviderDefinition(environment?.deploymentServiceConnectionType)?.tabLabel
		?? environment?.deploymentServiceConnectionType
		?? 'Unknown';

	// Initialize form data when environment changes
	useEffect(() => {
		if (environment) {
			setFormData({
				name: environment.name,
				tags: environment.tags || [],
			});
		}
	}, [environment]);

	const handleClose = () => {
		if (!isSaving) {
			setOpen(false);
		}
	};

	const handleSave = async () => {
		if (!environment) return;

		if (!formData.name?.trim()) {
			toastService.error('Environment name is required');
			return;
		}

		setIsSaving(true);
		try {
			// TODO: Update API to support attributes in UpdateEnvironmentRequest
			// For now, only updating basic fields (name, tags, order)
			await client.update(environment.id, formData);
			toastService.success(`Environment "${formData.name}" updated successfully`);
			onSuccess();
			handleClose();
		} catch (error) {
			console.error('Error updating environment:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to update environment: ${errorMessage}`);
		} finally {
			setIsSaving(false);
		}
	};

	const handleAddTag = () => {
		if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
			setFormData((prev) => ({
				...prev,
				tags: [...(prev.tags || []), tagInput.trim()],
			}));
			setTagInput('');
		}
	};

	const handleRemoveTag = (tagToRemove: string) => {
		setFormData((prev) => ({
			...prev,
			tags: prev.tags?.filter((tag) => tag !== tagToRemove) || [],
		}));
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleAddTag();
		}
	};

	if (!environment) return null;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Edit Environment</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ pt: 1 }}>
					<TextField
						label="Environment Name"
						value={formData.name || ''}
						onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
						fullWidth
						required
						placeholder="e.g., Development, Staging, Production"
					/>

					<TextField label="Deployment Provider" value={deploymentProvider} fullWidth slotProps={{ input: { readOnly: true } }} />

					<TextField
						label="Primary Service Connection"
						value={isLoadingPrimaryServiceConnection ? 'Loading...' : primaryServiceConnection?.name ?? environment.serviceConnectionId}
						fullWidth
						helperText="Deployment provider changes require an environment migration."
						slotProps={{ input: { readOnly: true } }}
					/>

					<Box>
						<TextField
							label="Add Tag"
							value={tagInput}
							onChange={(e) => setTagInput(e.target.value)}
							onKeyPress={handleKeyPress}
							fullWidth
							placeholder="Enter a tag and press Enter"
							InputProps={{
								endAdornment: (
									<Button onClick={handleAddTag} disabled={!tagInput.trim()} size="small" startIcon={<AddCircle />}>
										Add
									</Button>
								),
							}}
						/>

						<Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
							{formData.tags?.map((tag) => (
								<Chip key={tag} label={tag} onDelete={() => handleRemoveTag(tag)} size="small" variant="outlined" />
							))}
						</Box>
					</Box>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isSaving} sx={{ mr: 1 }}>
					Cancel
				</Button>
				<Button onClick={handleSave} variant="contained" startIcon={<Save />} disabled={isSaving || !formData.name?.trim()}>
					{isSaving ? 'Saving...' : 'Save Changes'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
